// First Created - 04 Sep 2026
// Author - Atul Pandey
// Purpose - RTK Query API slice for Trinity Intention Board
//           candidate reviews and reviewer decisions.
// Updated - 07 Sep 2026 - Added getOrgIntentionCandidateReviewMatrix endpoint
// Updated - 10 Sep 2026 - Added finalizeOrgIntentionCandidateDecision endpoint
// Updated - 16 Sep 2026 - Added confirmOrgIntentionCandidateDecision endpoint

import {
  createSelector,
  createEntityAdapter,
} from '@reduxjs/toolkit'

import {
  apiSlice,
} from '@/app/api/apiSlice'

/*
|--------------------------------------------------------------------------
| Entity Adapter
|--------------------------------------------------------------------------
*/

const orgIntentionCandidateReviewsAdapter =
  createEntityAdapter({})

const initialState =
  orgIntentionCandidateReviewsAdapter
    .getInitialState()

/*
|--------------------------------------------------------------------------
| API Slice
|--------------------------------------------------------------------------
*/

export const orgIntentionCandidateReviewsApiSlice =
  apiSlice.injectEndpoints({

    endpoints:
      builder => ({

        /*
        |--------------------------------------------------------------------------
        | Get Intention Candidate Reviews
        |--------------------------------------------------------------------------
        |
        | Private
        |
        | Returns all active reviews for an Intention Board.
        |
        */

        getOrgIntentionCandidateReviews:
          builder.query({

            query:
              ({
                intentionBoardId,
                reviewDecision,
              }) => ({

                url:
                  '/orgIntentionCandidateReview/getOrgIntentionCandidateReviews',

                params: {

                  intentionBoardId,

                  ...(reviewDecision
                    ? {
                        reviewDecision,
                      }
                    : {}),

                },

                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),

            transformResponse:
              responseData => {

                const loadedReviews =
                  responseData.map(
                    review => {

                      review.id =
                        review._id

                      return review

                    }
                  )

                return orgIntentionCandidateReviewsAdapter
                  .setAll(
                    initialState,
                    loadedReviews
                  )

              },

            providesTags:
              (
                result,
                error,
                arg
              ) => {

                if (
                  result?.ids
                ) {

                  return [

                    {
                      type:
                        'OrgIntentionCandidateReview',

                      id:
                        `LIST-${arg?.intentionBoardId}`,
                    },

                    ...result.ids.map(
                      id => ({

                        type:
                          'OrgIntentionCandidateReview',

                        id,

                      })
                    ),

                  ]

                }

                return [

                  {
                    type:
                      'OrgIntentionCandidateReview',

                    id:
                      `LIST-${arg?.intentionBoardId}`,
                  },

                ]

              },

          }),

        /*
        |--------------------------------------------------------------------------
        | Get Reviews By Candidate
        |--------------------------------------------------------------------------
        |
        | Private
        |
        | Returns:
        |
        | {
        |   candidate,
        |   reviews
        | }
        |
        */

        getOrgIntentionCandidateReviewsByCandidate:
          builder.query({

            query:
              ({
                intentionBoardId,
                intentionCandidateId,
              }) => ({

                url:
                  '/orgIntentionCandidateReview/getOrgIntentionCandidateReviewsByCandidate',

                params: {
                  intentionBoardId,
                  intentionCandidateId,
                },

                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),

            providesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type:
                    'OrgIntentionCandidateReview',

                  id:
                    `CANDIDATE-${arg?.intentionCandidateId}`,
                },

                {
                  type:
                    'OrgIntentionCandidate',

                  id:
                    arg?.intentionCandidateId,
                },

              ],

          }),
        
        /*
        |--------------------------------------------------------------------------
        | Get Intention Candidate Review Matrix - 07 Sep 2026
        |--------------------------------------------------------------------------
        |
        | Private
        |
        | Returns:
        |
        | {
        |   board,
        |   summary,
        |   candidates
        | }
        |
        */

        getOrgIntentionCandidateReviewMatrix:
           builder.query({

                query:
                ({
                    intentionBoardId,
                }) => ({

                    url:
                    '/orgIntentionCandidateReview/getOrgIntentionCandidateReviewMatrix',

                    params: {

                    intentionBoardId,

                    },

                    validateStatus:
                    (
                        response,
                        result
                    ) => {

                        return (
                        response.status ===
                            200 &&
                        !result.isError
                        )

                    },

                }),

                providesTags:
                (
                    result,
                    error,
                    arg
                ) => [

                    {
                    type:
                        'OrgIntentionCandidateReview',

                    id:
                        `MATRIX-${arg?.intentionBoardId}`,
                    },

                    {
                    type:
                        'OrgIntentionCandidate',

                    id:
                        `LIST-${arg?.intentionBoardId}`,
                    },

                ],

           }),

        /*
        |--------------------------------------------------------------------------
        | Get My Candidate Review
        |--------------------------------------------------------------------------
        |
        | Private
        |
        | Returns:
        |
        | review object
        |
        | or:
        |
        | null
        |
        */

        getMyOrgIntentionCandidateReview:
          builder.query({

            query:
              ({
                intentionBoardId,
                intentionCandidateId,
              }) => ({

                url:
                  '/orgIntentionCandidateReview/getMyOrgIntentionCandidateReview',

                params: {
                  intentionBoardId,
                  intentionCandidateId,
                },

                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),

            providesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type:
                    'OrgIntentionCandidateReview',

                  id:
                    `MY-${arg?.intentionCandidateId}`,
                },

              ],

          }),

        /*
        |--------------------------------------------------------------------------
        | Create Or Update Candidate Review
        |--------------------------------------------------------------------------
        |
        | Private
        |
        | One review exists per:
        |
        | board
        | candidate
        | reviewer
        |
        */

        createOrUpdateOrgIntentionCandidateReview:
          builder.mutation({

            query:
              initialReview => ({

                url:
                  '/orgIntentionCandidateReview/createOrUpdateOrgIntentionCandidateReview',

                method:
                  'POST',

                body: {
                  ...initialReview,
                },

              }),

            invalidatesTags:
              (
                result,
                error,
                arg
              ) => {

                const reviewId =
                  result?.review?._id

                const candidateId =
                  result?.candidate
                    ?.intentionCandidateId ||
                  arg?.intentionCandidateId

                return [

                  {
                    type:
                      'OrgIntentionCandidateReview',

                    id:
                      `LIST-${arg?.intentionBoardId}`,
                  },


                  // 07 Sep 2026
                  {
                    type:
                        'OrgIntentionCandidateReview',

                    id:
                        `MATRIX-${arg?.intentionBoardId}`,
                  },

                  {
                    type:
                      'OrgIntentionCandidateReview',

                    id:
                      `CANDIDATE-${candidateId}`,
                  },

                  {
                    type:
                      'OrgIntentionCandidateReview',

                    id:
                      `MY-${candidateId}`,
                  },

                  ...(reviewId
                    ? [
                        {
                          type:
                            'OrgIntentionCandidateReview',

                          id:
                            reviewId,
                        },
                      ]
                    : []),

                  ...(candidateId
                    ? [
                        {
                          type:
                            'OrgIntentionCandidate',

                          id:
                            candidateId,
                        },
                      ]
                    : []),

                  ...(arg?.intentionBoardId
                    ? [
                        {
                          type:
                            'OrgIntentionCandidate',

                          id:
                            `LIST-${arg.intentionBoardId}`,
                        },
                      ]
                    : []),

                ]

              },

          }),

        /*
        |--------------------------------------------------------------------------
        | Confirm Candidate Decision - 16 Sep 2026
        |--------------------------------------------------------------------------
        |
        | Private
        |
        | Confirms the formal candidate decision without finalizing the
        | candidate lifecycle.
        |
        | Sets:
        |
        | finalDecision
        | decisionByUserId
        | decisionAt
        |
        | Does NOT set:
        |
        | decisionStatus = finalized
        |
        */

        confirmOrgIntentionCandidateDecision:
          builder.mutation({

            query:
              initialDecision => ({

                url:
                  '/orgIntentionCandidateReview/confirmOrgIntentionCandidateDecision',

                method:
                  'POST',

                body: {
                  ...initialDecision,
                },

              }),


            invalidatesTags:
              (
                result,
                error,
                arg
              ) => {

                const candidateId =
                  result
                    ?.candidate
                    ?.intentionCandidateId ||
                  arg
                    ?.intentionCandidateId


                return [

                  /*
                  |--------------------------------------------------------------------------
                  | Review Matrix
                  |--------------------------------------------------------------------------
                  */

                  {
                    type:
                      'OrgIntentionCandidateReview',

                    id:
                      `MATRIX-${arg?.intentionBoardId}`,
                  },


                  /*
                  |--------------------------------------------------------------------------
                  | Candidate Reviews
                  |--------------------------------------------------------------------------
                  */

                  ...(candidateId
                    ? [

                        {
                          type:
                            'OrgIntentionCandidateReview',

                          id:
                            `CANDIDATE-${candidateId}`,
                        },

                        {
                          type:
                            'OrgIntentionCandidateReview',

                          id:
                            `MY-${candidateId}`,
                        },

                      ]
                    : []),


                  /*
                  |--------------------------------------------------------------------------
                  | Board Review List
                  |--------------------------------------------------------------------------
                  */

                  ...(arg
                    ?.intentionBoardId
                    ? [

                        {
                          type:
                            'OrgIntentionCandidateReview',

                          id:
                            `LIST-${arg.intentionBoardId}`,
                        },

                      ]
                    : []),


                  /*
                  |--------------------------------------------------------------------------
                  | Candidate
                  |--------------------------------------------------------------------------
                  */

                  ...(candidateId
                    ? [

                        {
                          type:
                            'OrgIntentionCandidate',

                          id:
                            candidateId,
                        },

                      ]
                    : []),


                  /*
                  |--------------------------------------------------------------------------
                  | Candidate List
                  |--------------------------------------------------------------------------
                  */

                  ...(arg
                    ?.intentionBoardId
                    ? [

                        {
                          type:
                            'OrgIntentionCandidate',

                          id:
                            `LIST-${arg.intentionBoardId}`,
                        },

                      ]
                    : []),

                ]

              },

          }),

        /*
        |--------------------------------------------------------------------------
        | Finalize Candidate Decision - 10 Sep 2026
        |--------------------------------------------------------------------------
        |
        | Private
        |
        | Formally finalizes one submitted candidate decision.
        |
        */

        finalizeOrgIntentionCandidateDecision:
          builder.mutation({

            query:
              initialDecision => ({

                url:
                  '/orgIntentionCandidateReview/finalizeOrgIntentionCandidateDecision',

                method:
                  'POST',

                body: {
                  ...initialDecision,
                },

              }),

            invalidatesTags:
              (
                result,
                error,
                arg
              ) => {

                const candidateId =
                  result
                    ?.candidate
                    ?.intentionCandidateId ||
                  arg
                    ?.intentionCandidateId

                return [

                  /*
                  |--------------------------------------------------------------------------
                  | Review Matrix
                  |--------------------------------------------------------------------------
                  */

                  {
                    type:
                      'OrgIntentionCandidateReview',

                    id:
                      `MATRIX-${arg?.intentionBoardId}`,
                  },

                  /*
                  |--------------------------------------------------------------------------
                  | Candidate Reviews
                  |--------------------------------------------------------------------------
                  */

                  ...(candidateId
                    ? [
                        {
                          type:
                            'OrgIntentionCandidateReview',

                          id:
                            `CANDIDATE-${candidateId}`,
                        },

                        {
                          type:
                            'OrgIntentionCandidateReview',

                          id:
                            `MY-${candidateId}`,
                        },
                      ]
                    : []),

                  /*
                  |--------------------------------------------------------------------------
                  | Board Review List
                  |--------------------------------------------------------------------------
                  */

                  ...(arg
                    ?.intentionBoardId
                    ? [
                        {
                          type:
                            'OrgIntentionCandidateReview',

                          id:
                            `LIST-${arg.intentionBoardId}`,
                        },
                      ]
                    : []),

                  /*
                  |--------------------------------------------------------------------------
                  | Candidate
                  |--------------------------------------------------------------------------
                  */

                  ...(candidateId
                    ? [
                        {
                          type:
                            'OrgIntentionCandidate',

                          id:
                            candidateId,
                        },
                      ]
                    : []),

                  /*
                  |--------------------------------------------------------------------------
                  | Candidate List
                  |--------------------------------------------------------------------------
                  */

                  ...(arg
                    ?.intentionBoardId
                    ? [
                        {
                          type:
                            'OrgIntentionCandidate',

                          id:
                            `LIST-${arg.intentionBoardId}`,
                        },
                      ]
                    : []),

                ]

              },

          }),

        /*
        |--------------------------------------------------------------------------
        | Delete Candidate Review
        |--------------------------------------------------------------------------
        |
        | Private
        |
        */

        deleteOrgIntentionCandidateReview:
          builder.mutation({

            query:
              ({
                intentionCandidateReviewId,
              }) => ({

                url:
                  '/orgIntentionCandidateReview/deleteOrgIntentionCandidateReview',

                method:
                  'DELETE',

                body: {
                  intentionCandidateReviewId,
                },

              }),

            invalidatesTags:
              (
                result,
                error,
                arg
              ) => {

                const candidateId =
                  result?.candidate
                    ?.intentionCandidateId ||
                  arg?.intentionCandidateId

                return [

                  {
                    type:
                      'OrgIntentionCandidateReview',

                    id:
                      arg?.intentionCandidateReviewId,
                  },

                  ...(arg?.intentionBoardId
                    ? [
                        {
                          type:
                            'OrgIntentionCandidateReview',

                          id:
                            `LIST-${arg.intentionBoardId}`,
                        },
                      ]
                    : []),

                  // 07 Sep 2026
                  ...(arg?.intentionBoardId
                    ? [
                        {
                            type:
                            'OrgIntentionCandidateReview',

                            id:
                            `MATRIX-${arg.intentionBoardId}`,
                        },
                        ]
                    : []),  

                  ...(candidateId
                    ? [
                        {
                          type:
                            'OrgIntentionCandidateReview',

                          id:
                            `CANDIDATE-${candidateId}`,
                        },

                        {
                          type:
                            'OrgIntentionCandidateReview',

                          id:
                            `MY-${candidateId}`,
                        },

                        {
                          type:
                            'OrgIntentionCandidate',

                          id:
                            candidateId,
                        },
                      ]
                    : []),

                  ...(arg?.intentionBoardId
                    ? [
                        {
                          type:
                            'OrgIntentionCandidate',

                          id:
                            `LIST-${arg.intentionBoardId}`,
                        },
                      ]
                    : []),

                ]

              },

          }),

      }),

  })

/*
|--------------------------------------------------------------------------
| Hooks
|--------------------------------------------------------------------------
*/

export const {

  useGetOrgIntentionCandidateReviewsQuery,

  useGetOrgIntentionCandidateReviewsByCandidateQuery,

  // 07 Sep 2026
  useGetOrgIntentionCandidateReviewMatrixQuery,         

  useGetMyOrgIntentionCandidateReviewQuery,

  useCreateOrUpdateOrgIntentionCandidateReviewMutation,

  // 16 Sep 2026
  useConfirmOrgIntentionCandidateDecisionMutation,

  // 10 Sep 2026
  useFinalizeOrgIntentionCandidateDecisionMutation,

  useDeleteOrgIntentionCandidateReviewMutation,

} =
  orgIntentionCandidateReviewsApiSlice

/*
|--------------------------------------------------------------------------
| Board Review Query Result Selector
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionCandidateReviewsResult =
  (
    intentionBoardId,
    reviewDecision
  ) =>
    orgIntentionCandidateReviewsApiSlice
      .endpoints
      .getOrgIntentionCandidateReviews
      .select({

        intentionBoardId,

        ...(reviewDecision
          ? {
              reviewDecision,
            }
          : {}),

      })

/*
|--------------------------------------------------------------------------
| Board Review Data Selector
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionCandidateReviewsData =
  (
    intentionBoardId,
    reviewDecision
  ) =>
    createSelector(

      selectOrgIntentionCandidateReviewsResult(
        intentionBoardId,
        reviewDecision
      ),

      reviewsResult =>
        reviewsResult.data

    )

/*
|--------------------------------------------------------------------------
| Entity Selectors
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionCandidateReviewSelectors =
  (
    intentionBoardId,
    reviewDecision
  ) =>
    orgIntentionCandidateReviewsAdapter
      .getSelectors(

        state => {

          const reviewData =
            selectOrgIntentionCandidateReviewsData(
              intentionBoardId,
              reviewDecision
            )(
              state
            )

          return (
            reviewData ??
            initialState
          )

        }

      )